import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Input,
    Textarea,
    Select,
    Option,
    Chip,
    IconButton,
    Spinner
} from "@material-tailwind/react";
import {
    ArrowLeftIcon,
    PencilIcon,
    TrashIcon,
    ClockIcon,
    ChatBubbleLeftIcon,
    DocumentTextIcon,
    ListBulletIcon,
    ArrowsRightLeftIcon
} from "@heroicons/react/24/outline";
import TaskCommentSection from './TaskCommentSection';
import TaskTimeSection from './TaskTimeSection';
import TaskActivitySection from './TaskActivitySection';
import TaskAssigneeSelector from './TaskAssigneeSelector';
import TaskChecklistSection from './TaskChecklistSection';
import TaskDependencySection from './TaskDependencySection';
import ConfirmModal from '../../common/ConfirmModal';
import TaskWorkspaceShell from './TaskWorkspaceShell';
import TaskTabsBar from './TaskTabsBar';

const TaskDetail = () => {
    const { taskId } = useParams();
    const navigate = useNavigate();
    const { rootUrl } = require('../../../config/config');
    const [token] = useState(localStorage.getItem('authToken') || '');

    const [task, setTask] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');

    const [editForm, setEditForm] = useState({
        title: '',
        description: '',
        priority: '',
        status: '',
        due_date: '',
        estimated_hours: ''
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchTask();
    }, [taskId]);

    const fetchTask = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${rootUrl}/api/tasks/${taskId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                const taskData = response.data.data;
                setTask(taskData);
                setEditForm({
                    title: taskData.title,
                    description: taskData.description || '',
                    priority: taskData.priority,
                    status: taskData.status,
                    due_date: taskData.due_date ? taskData.due_date.split('T')[0] : '',
                    estimated_hours: taskData.estimated_hours || ''
                });
            }
        } catch (error) {
            console.error('Error fetching task:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = () => {
        setEditing(true);
    };

    const handleCancelEdit = () => {
        setEditing(false);
        setEditForm({
            title: task.title,
            description: task.description || '',
            priority: task.priority,
            status: task.status,
            due_date: task.due_date ? task.due_date.split('T')[0] : '',
            estimated_hours: task.estimated_hours || ''
        });
    };

    const handleSaveEdit = async () => {
        try {
            const response = await axios.put(
                `${rootUrl}/api/tasks/${taskId}`,
                { ...editForm, updated_by: 'current_user' },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setTask(response.data.data);
                setEditing(false);
            }
        } catch (error) {
            console.error('Error updating task:', error);
        }
    };

    const handleDelete = async () => {
        setDeleteLoading(true);
        setDeleteError('');

        try {
            const response = await axios.delete(
                `${rootUrl}/api/tasks/${taskId}?deleted_by=current_user`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                navigate('/tasks');
            } else {
                setDeleteError(response.data.message || 'Failed to delete task');
            }
        } catch (error) {
            console.error('Error deleting task:', error);
            setDeleteError(error?.response?.data?.message || 'Failed to delete task');
        } finally {
            setDeleteLoading(false);
        }
    };

    const getPriorityColor = (priority) => {
        const colors = {
            low: 'blue-gray',
            normal: 'blue',
            high: 'orange',
            urgent: 'red'
        };
        return colors[priority] || 'gray';
    };

    const getStatusColor = (status) => {
        const colors = {
            'open': 'gray',
            'To Do': 'gray',
            'In Progress': 'blue',
            'Review': 'orange',
            'Done': 'green',
            'Completed': 'green'
        };
        return colors[status] || 'gray';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const tabItems = [
        { id: 'details', label: 'Details', icon: DocumentTextIcon },
        { id: 'comments', label: `Comments (${task?.comment_count || 0})`, icon: ChatBubbleLeftIcon },
        { id: 'time', label: 'Time Tracking', icon: ClockIcon },
        { id: 'activity', label: 'Activity', icon: DocumentTextIcon },
        { id: 'checklists', label: 'Checklists', icon: ListBulletIcon },
        { id: 'dependencies', label: 'Dependencies', icon: ArrowsRightLeftIcon },
    ];

    if (loading) {
        return (
            <TaskWorkspaceShell>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <Spinner className="h-12 w-12" color="blue" />
                </div>
            </TaskWorkspaceShell>
        );
    }

    if (!task) {
        return (
            <TaskWorkspaceShell>
                <div className="flex items-center justify-center min-h-[60vh]">
                    <Typography>Task not found</Typography>
                </div>
            </TaskWorkspaceShell>
        );
    }

    return (
        <TaskWorkspaceShell>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <Button
                        variant="text"
                        className="flex items-center gap-2"
                        onClick={() => navigate(-1)}
                    >
                        <ArrowLeftIcon className="h-4 w-4" />
                        Back
                    </Button>

                    <div className="flex gap-2">
                        {!editing && (
                            <>
                                <IconButton
                                    variant="text"
                                    color="blue"
                                    onClick={handleEdit}
                                >
                                    <PencilIcon className="h-5 w-5" />
                                </IconButton>
                                <IconButton
                                    variant="text"
                                    color="red"
                                    onClick={() => setShowDeleteConfirm(true)}
                                >
                                    <TrashIcon className="h-5 w-5" />
                                </IconButton>
                            </>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content */}
                    <div className="lg:col-span-2 space-y-6">
                        <Card>
                            <CardBody>
                                {editing ? (
                                    <div className="space-y-4">
                                        <Input
                                            label="Task Title"
                                            value={editForm.title}
                                            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                                        />
                                        <Textarea
                                            label="Description"
                                            value={editForm.description}
                                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                            rows={6}
                                        />
                                        <div className="grid grid-cols-2 gap-4">
                                            <Select
                                                label="Priority"
                                                value={editForm.priority}
                                                onChange={(val) => setEditForm({ ...editForm, priority: val })}
                                            >
                                                <Option value="low">Low</Option>
                                                <Option value="normal">Normal</Option>
                                                <Option value="high">High</Option>
                                                <Option value="urgent">Urgent</Option>
                                            </Select>
                                            <Select
                                                label="Status"
                                                value={editForm.status}
                                                onChange={(val) => setEditForm({ ...editForm, status: val })}
                                            >
                                                <Option value="open">Open</Option>
                                                <Option value="To Do">To Do</Option>
                                                <Option value="In Progress">In Progress</Option>
                                                <Option value="Review">Review</Option>
                                                <Option value="Done">Done</Option>
                                            </Select>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <Input
                                                type="date"
                                                label="Due Date"
                                                value={editForm.due_date}
                                                onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })}
                                            />
                                            <Input
                                                type="number"
                                                label="Estimated Hours"
                                                value={editForm.estimated_hours}
                                                onChange={(e) => setEditForm({ ...editForm, estimated_hours: e.target.value })}
                                            />
                                        </div>
                                        <div className="flex gap-2 justify-end">
                                            <Button variant="text" onClick={handleCancelEdit}>
                                                Cancel
                                            </Button>
                                            <Button
                                                className="bg-[#FF5733]"
                                                onClick={handleSaveEdit}
                                            >
                                                Save Changes
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div>
                                            <Typography variant="h4" color="blue-gray" className="font-bold">
                                                {task.title}
                                            </Typography>
                                            <div className="flex gap-2 mt-2">
                                                <Chip
                                                    size="sm"
                                                    value={task.priority}
                                                    color={getPriorityColor(task.priority)}
                                                    className="capitalize"
                                                />
                                                <Chip
                                                    size="sm"
                                                    value={task.status_name || task.status}
                                                    color={getStatusColor(task.status_name || task.status)}
                                                />
                                            </div>
                                        </div>

                                        {task.description && (
                                            <div>
                                                <Typography variant="h6" color="blue-gray" className="mb-2">
                                                    Description
                                                </Typography>
                                                <Typography color="gray" className="whitespace-pre-wrap">
                                                    {task.description}
                                                </Typography>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardBody>
                        </Card>

                        <Card>
                            <CardBody>
                                <TaskTabsBar tabs={tabItems} activeTab={activeTab} onChange={setActiveTab} />
                                {activeTab === 'details' && (
                                    <div className="space-y-3">
                                        <Typography variant="small" color="gray">
                                            Additional task information will be displayed here.
                                        </Typography>
                                    </div>
                                )}
                                {activeTab === 'comments' && <TaskCommentSection taskId={taskId} />}
                                {activeTab === 'time' && <TaskTimeSection taskId={taskId} />}
                                {activeTab === 'activity' && <TaskActivitySection taskId={taskId} />}
                                {activeTab === 'checklists' && <TaskChecklistSection taskId={taskId} />}
                                {activeTab === 'dependencies' && <TaskDependencySection taskId={taskId} projectId={task.project_id} />}
                            </CardBody>
                        </Card>
                    </div>

                    {/* Sidebar */}
                    <div className="space-y-6">
                        {/* Assignees Card */}
                        <Card>
                            <CardBody>
                                <TaskAssigneeSelector
                                    taskId={taskId}
                                    currentAssignees={task.assignees || []}
                                    onAssigneeChange={(updated) => {
                                        setTask({ ...task, assignees: updated });
                                    }}
                                />
                            </CardBody>
                        </Card>

                        <Card>
                            <CardBody>
                                <Typography variant="h6" color="blue-gray" className="mb-4">
                                    Task Info
                                </Typography>
                                <div className="space-y-3">
                                    <div>
                                        <Typography variant="small" color="gray" className="font-semibold">
                                            Due Date
                                        </Typography>
                                        <Typography variant="small">
                                            {formatDate(task.due_date)}
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="small" color="gray" className="font-semibold">
                                            Estimated Hours
                                        </Typography>
                                        <Typography variant="small">
                                            {task.estimated_hours || 'Not set'} hours
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="small" color="gray" className="font-semibold">
                                            Actual Hours
                                        </Typography>
                                        <Typography variant="small">
                                            {task.actual_hours || 0} hours
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="small" color="gray" className="font-semibold">
                                            Created
                                        </Typography>
                                        <Typography variant="small">
                                            {formatDate(task.created_at)}
                                        </Typography>
                                    </div>
                                    {task.completed_at && (
                                        <div>
                                            <Typography variant="small" color="gray" className="font-semibold">
                                                Completed
                                            </Typography>
                                            <Typography variant="small">
                                                {formatDate(task.completed_at)}
                                            </Typography>
                                        </div>
                                    )}
                                </div>
                            </CardBody>
                        </Card>

                        {/* Progress Card */}
                        {task.total_checklist_items > 0 && (
                            <Card>
                                <CardBody>
                                    <Typography variant="h6" color="blue-gray" className="mb-4">
                                        Progress
                                    </Typography>
                                    <div className="space-y-2">
                                        <div className="flex justify-between text-sm">
                                            <span>Checklist</span>
                                            <span>{task.completed_checklist_items}/{task.total_checklist_items}</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full h-2">
                                            <div
                                                className="bg-[#FF5733] h-2 rounded-full"
                                                style={{
                                                    width: `${(task.completed_checklist_items / task.total_checklist_items) * 100}%`
                                                }}
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        )}
                    </div>
                </div>
            </div>

            <ConfirmModal
                isOpen={showDeleteConfirm}
                title="Delete task"
                description={`This will permanently delete "${task.title}". This action cannot be undone.`}
                onCancel={() => {
                    setShowDeleteConfirm(false);
                    setDeleteError('');
                }}
                onConfirm={handleDelete}
                confirmText="Delete"
                cancelText="Cancel"
                loading={deleteLoading}
                error={deleteError}
                variant="danger"
            />
        </TaskWorkspaceShell>
    );
};

export default TaskDetail;
